import type { ProjectViewPrefs } from '@vd/types';

/**
 * The view choices a board keeps across visits, per project, in this browser
 * (board ticket t-hMetLW: the sort went back to its default on every reload).
 *
 * Only how the board is shown is kept: the sort, the view, thumbnails and the
 * sidebar. Filters are not, so a filter left on cannot hide tickets the next
 * time the board opens. Storage can be missing or refuse a write (private
 * windows, a full quota); then the board simply starts from its defaults.
 */

export type RememberedViewPrefs = Pick<ProjectViewPrefs, 'viewMode' | 'thumbnails' | 'sort' | 'sidebarCollapsed'>;

const VIEW_MODES: ReadonlyArray<ProjectViewPrefs['viewMode']> = ['board', 'cards', 'table', 'single'];
const SORTS: ReadonlyArray<ProjectViewPrefs['sort']> = ['user-sorted', 'newest', 'oldest', 'last-activity', 'most-comments'];

function storageKey(projectId: string): string {
  return `toado:board-view:${projectId}`;
}

/** What this browser remembers for the project; only values it can still use. */
export function readRememberedViewPrefs(projectId: string): Partial<RememberedViewPrefs> {
  try {
    const raw = window.localStorage.getItem(storageKey(projectId));
    if (!raw) return {};
    const stored: unknown = JSON.parse(raw);
    if (!stored || typeof stored !== 'object') return {};
    const value = stored as Record<string, unknown>;
    const remembered: Partial<RememberedViewPrefs> = {};
    if (VIEW_MODES.includes(value.viewMode as ProjectViewPrefs['viewMode'])) remembered.viewMode = value.viewMode as ProjectViewPrefs['viewMode'];
    if (SORTS.includes(value.sort as ProjectViewPrefs['sort'])) remembered.sort = value.sort as ProjectViewPrefs['sort'];
    if (typeof value.thumbnails === 'boolean') remembered.thumbnails = value.thumbnails;
    if (typeof value.sidebarCollapsed === 'boolean') remembered.sidebarCollapsed = value.sidebarCollapsed;
    return remembered;
  } catch {
    return {};
  }
}

/** Keep the project's view choices from these prefs. */
export function rememberViewPrefs(projectId: string, prefs: ProjectViewPrefs): void {
  const remembered: RememberedViewPrefs = {
    viewMode: prefs.viewMode,
    thumbnails: prefs.thumbnails,
    sort: prefs.sort,
    sidebarCollapsed: prefs.sidebarCollapsed,
  };
  try {
    window.localStorage.setItem(storageKey(projectId), JSON.stringify(remembered));
  } catch {
    // Not kept this time; the choice still holds for this visit.
  }
}
